import React, { useEffect, useRef, useState, useCallback } from 'react';
// Lazy-loaded ffmpeg.wasm for CDG fallback (new API)
let ffmpegModule: any | null = null; // { FFmpeg }
let ffmpegUtil: any | null = null;   // { toBlobURL }

// Minimal CD+G renderer (experimental): supports color tables, memory/border preset,
// and tile block normal/XOR. Scroll commands are ignored for now.
// Timing: ~300 CDG packets per second. We advance based on audio currentTime.
// This is a best‑effort implementation and may not render all discs perfectly.

type Props = {
  mp3Url: string;
  cdgBuffer: ArrayBuffer;
  onEnded?: () => void;
};

// CD+G nominal frame is 300x216. Active tiles are within a 294x204 region
// offset by a 6px left/right and 12px top/bottom border. We render full size
// and respect the border region for Border Preset commands.
const WIDTH = 300;
const HEIGHT = 216;
const BORDER_X = 6;   // left border thickness in pixels
const BORDER_Y = 12;  // top border thickness in pixels
const ACTIVE_W = WIDTH - BORDER_X * 2;  // 294
const ACTIVE_H = HEIGHT - BORDER_Y * 2; // 204
const TILE_W = 6;
const TILE_H = 12;
// Heuristic: clear pixels that change color due to palette updates to prevent lingering lines
const CLEAR_ON_PALETTE_CHANGE = true;

function nibble4(b: number) { return b & 0x0F; }
function color12ToRgb(c: number) {
  // CDG colors are 4 bits per channel; scale 0..15 -> 0..255
  const r = ((c >> 8) & 0x0F) * 17;
  const g = ((c >> 4) & 0x0F) * 17;
  const b = (c & 0x0F) * 17;
  return [r, g, b];
}

const CdgPlayer: React.FC<Props> = ({ mp3Url, cdgBuffer, onEnded }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const packetsRef = useRef<Uint8Array | null>(null);
  const paletteRef = useRef<Uint8ClampedArray>(new Uint8ClampedArray(16 * 3));
  const displayPaletteRef = useRef<Uint8ClampedArray>(new Uint8ClampedArray(16 * 3));
  const frameRef = useRef<Uint8ClampedArray>(new Uint8ClampedArray(WIDTH * HEIGHT * 4));
  // Keep a parallel buffer of palette indices so XOR operations work on color indexes
  const indexRef = useRef<Uint8Array>(new Uint8Array(WIDTH * HEIGHT));
  const lastIndexRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const transparentIndexRef = useRef<number>(0x00);
  const memoryColorRef = useRef<number | null>(null);
  const borderColorRef = useRef<number | null>(null);
  const anyDrawRef = useRef<boolean>(false);
  const paletteModeRef = useRef<0 | 1>(0); // 0 = spec nibble order, 1 = legacy nibble order fallback
  const paletteFallbackTriedRef = useRef<boolean>(false);
  const statsRef = useRef<{ tiles: number; palettes: number; presets: number; scrolls: number }>(
    { tiles: 0, palettes: 0, presets: 0, scrolls: 0 }
  );
  const lastOpsRef = useRef<number[]>([]); // last few instruction codes for debugging
  const lastTileRef = useRef<{col:number;row:number;op:number}|null>(null);
  const lastScrollRef = useRef<{dx:number;dy:number;op:number}|null>(null);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [ready, setReady] = useState(false);
  // Debug UI removed
  // Fallback state
  const [usingFfmpegFallback, setUsingFfmpegFallback] = useState(false);
  const [ffmpegDecoding, setFfmpegDecoding] = useState(false);
  const ffmpegFramesRef = useRef<{bitmap: ImageBitmap; url: string}[] | null>(null);
  const ffmpegFpsRef = useRef<number>(15);
  const ffmpegAttemptedRef = useRef<boolean>(false);
  const ffmpegFailedRef = useRef<boolean>(false);

  // Recolor lyrics: map likely lyric indices to white/yellow in a display palette
  const RECOLOR_LYRICS = true;
  const recomputeDisplayPalette = useCallback(() => {
    // Start with an exact copy of the current CLUT
    for (let i = 0; i < 16 * 3; i++) displayPaletteRef.current[i] = paletteRef.current[i];
    if (!RECOLOR_LYRICS) return;
    // Build usage counts for active area
    const counts = new Uint32Array(16);
    for (let y = BORDER_Y; y < HEIGHT - BORDER_Y; y++) {
      let base = y * WIDTH + BORDER_X;
      for (let x = 0; x < ACTIVE_W; x++, base++) {
        counts[indexRef.current[base] & 0x0F]++;
      }
    }
    const tIdx = transparentIndexRef.current & 0x0F;
    const bgIdxCandidates = new Set<number>();
    if (memoryColorRef.current != null) bgIdxCandidates.add(memoryColorRef.current & 0x0F);
    if (borderColorRef.current != null) bgIdxCandidates.add(borderColorRef.current & 0x0F);
    bgIdxCandidates.add(0); // often black

    // Prepare candidate list with luminance and usage
    type Cand = { idx: number; lum: number; count: number };
    const cand: Cand[] = [];
    for (let i = 0; i < 16; i++) {
      if (i === tIdx) continue;
      if (bgIdxCandidates.has(i)) continue;
      const r = paletteRef.current[i * 3 + 0];
      const g = paletteRef.current[i * 3 + 1];
      const b = paletteRef.current[i * 3 + 2];
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      cand.push({ idx: i, lum, count: counts[i] });
    }
    // Sort by usage primarily, luminance secondarily
    cand.sort((a, b) => (b.count - a.count) || (b.lum - a.lum));
    const chosen = cand.filter(c => c.count > 0 && c.lum > 40).slice(0, 2);
    if (chosen.length >= 1) {
      // Decide base vs highlight: brighter -> yellow, other -> white
      const [c0, c1] = chosen.length === 1 ? [chosen[0], null] : (chosen[0].lum >= chosen[1].lum ? [chosen[1], chosen[0]] : [chosen[0], chosen[1]]);
      if (c0) {
        // base (white)
        displayPaletteRef.current[c0.idx * 3 + 0] = 255;
        displayPaletteRef.current[c0.idx * 3 + 1] = 255;
        displayPaletteRef.current[c0.idx * 3 + 2] = 255;
      }
      if (c1) {
        // highlight (yellow)
        displayPaletteRef.current[c1.idx * 3 + 0] = 255;
        displayPaletteRef.current[c1.idx * 3 + 1] = 255;
        displayPaletteRef.current[c1.idx * 3 + 2] = 0;
      }
    }
  }, []);

  // Try to play audio, capturing autoplay errors for a tap-to-start UI
  const tryPlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    try {
      await el.play();
      setNeedsUserGesture(false);
    } catch (e) {
      // Chrome will reject if not triggered by a user gesture
      setNeedsUserGesture(true);
    }
  }, []);

  useEffect(() => {
    packetsRef.current = new Uint8Array(cdgBuffer);

    // Initialize palette to black
    for (let i = 0; i < 16; i++) {
      const [r, g, b] = [0, 0, 0];
      paletteRef.current[i * 3 + 0] = r;
      paletteRef.current[i * 3 + 1] = g;
      paletteRef.current[i * 3 + 2] = b;
    }
    // Sync display palette
    for (let i = 0; i < 16 * 3; i++) displayPaletteRef.current[i] = paletteRef.current[i];

    // Clear frame and index
    for (let i = 0; i < WIDTH * HEIGHT; i++) {
      const o = i * 4;
      frameRef.current[o + 0] = 0;
      frameRef.current[o + 1] = 0;
      frameRef.current[o + 2] = 0;
      frameRef.current[o + 3] = 255;
      indexRef.current[i] = 0;
    }

    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.createImageData(WIDTH, HEIGHT);

    const render = () => {
      const audio = audioRef.current;
      const pk = packetsRef.current;
      if (!audio || !pk) return;
      const t = audio.currentTime || 0;
      // If ffmpeg fallback is active, drive by frames and skip CDG packet loop
      if (usingFfmpegFallback) {
        const frames = ffmpegFramesRef.current;
        const fps = ffmpegFpsRef.current;
        if (frames && frames.length > 0) {
          const canvas = canvasRef.current!;
          const ctx = canvas.getContext('2d')!;
          const idx = Math.min(frames.length - 1, Math.max(0, Math.floor(t * fps)));
          const frame = frames[idx];
          ctx.clearRect(0, 0, WIDTH, HEIGHT);
          ctx.drawImage(frame.bitmap, 0, 0, WIDTH, HEIGHT);
        }
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      const targetIndex = Math.floor(t * 300); // 300 packets/sec

      // Apply packets up to targetIndex (clamped)
      const totalPackets = Math.floor(pk.length / 24);
      let idx = Math.min(targetIndex, totalPackets - 1);
      if (idx < lastIndexRef.current) {
        // If audio seeks backwards, reset state (simple approach)
        // For now, just reset everything and replay from 0 to idx
        lastIndexRef.current = 0;
        // Reset palette/frame to initial
        for (let i = 0; i < 16; i++) {
          paletteRef.current[i * 3 + 0] = 0;
          paletteRef.current[i * 3 + 1] = 0;
          paletteRef.current[i * 3 + 2] = 0;
        }
        for (let i = 0; i < WIDTH * HEIGHT; i++) {
          const o = i * 4;
          frameRef.current[o + 0] = 0;
          frameRef.current[o + 1] = 0;
          frameRef.current[o + 2] = 0;
          frameRef.current[o + 3] = 255;
          indexRef.current[i] = 0;
        }
        anyDrawRef.current = false;
      }

      while (lastIndexRef.current <= idx) {
        const off = lastIndexRef.current * 24;
        const cmd = pk[off + 0] & 0x3F; // lower 6 bits typically 0x09 for CDG
        const ins = pk[off + 1] & 0x3F;
        // bytes 2..17 data (16 bytes), 18..23 CRC/unused
        const dataOff = off + 4;
        if ((cmd & 0x3F) === 0x09) {
          // Track last few instruction codes for debugging
          lastOpsRef.current.push(ins);
          if (lastOpsRef.current.length > 12) lastOpsRef.current.shift();
          switch (ins) {
            case 0x01: { // Memory Preset (fill entire screen)
              const color = nibble4(pk[dataOff + 0]);
              memoryColorRef.current = color;
              recomputeDisplayPalette();
              const r = displayPaletteRef.current[color * 3 + 0];
              const g = displayPaletteRef.current[color * 3 + 1];
              const b = displayPaletteRef.current[color * 3 + 2];
              for (let y = 0; y < HEIGHT; y++) {
                for (let x = 0; x < WIDTH; x++) {
                  const idxp = (y * WIDTH + x);
                  const p = idxp * 4;
                  indexRef.current[idxp] = color;
                  frameRef.current[p + 0] = r;
                  frameRef.current[p + 1] = g;
                  frameRef.current[p + 2] = b;
                  frameRef.current[p + 3] = 255;
                }
              }
              statsRef.current.presets++;
              break;
            }
            case 0x02: { // Border Preset (true border regions only)
              const color = nibble4(pk[dataOff + 0]);
              borderColorRef.current = color;
              recomputeDisplayPalette();
              const r = displayPaletteRef.current[color * 3 + 0];
              const g = displayPaletteRef.current[color * 3 + 1];
              const b = displayPaletteRef.current[color * 3 + 2];
              // Only fill border region per spec
              // Top border
              for (let y = 0; y < BORDER_Y; y++) {
                for (let x = 0; x < WIDTH; x++) {
                  const idxp = (y * WIDTH + x);
                  const p = idxp * 4;
                  indexRef.current[idxp] = color;
                  frameRef.current[p + 0] = r;
                  frameRef.current[p + 1] = g;
                  frameRef.current[p + 2] = b;
                  frameRef.current[p + 3] = 255;
                }
              }
              // Bottom border
              for (let y = HEIGHT - BORDER_Y; y < HEIGHT; y++) {
                for (let x = 0; x < WIDTH; x++) {
                  const idxp = (y * WIDTH + x);
                  const p = idxp * 4;
                  indexRef.current[idxp] = color;
                  frameRef.current[p + 0] = r;
                  frameRef.current[p + 1] = g;
                  frameRef.current[p + 2] = b;
                  frameRef.current[p + 3] = 255;
                }
              }
              // Left + Right borders in the active rows
              for (let y = BORDER_Y; y < HEIGHT - BORDER_Y; y++) {
                for (let x = 0; x < BORDER_X; x++) {
                  const idxp = (y * WIDTH + x);
                  const p = idxp * 4;
                  indexRef.current[idxp] = color;
                  frameRef.current[p + 0] = r;
                  frameRef.current[p + 1] = g;
                  frameRef.current[p + 2] = b;
                  frameRef.current[p + 3] = 255;
                }
                for (let x = WIDTH - BORDER_X; x < WIDTH; x++) {
                  const idxp = (y * WIDTH + x);
                  const p = idxp * 4;
                  indexRef.current[idxp] = color;
                  frameRef.current[p + 0] = r;
                  frameRef.current[p + 1] = g;
                  frameRef.current[p + 2] = b;
                  frameRef.current[p + 3] = 255;
                }
              }
              break;
            }
            case 0x06: // Tile Block (Normal)
            case 0x26: { // Tile Block XOR (38)
              const color0 = nibble4(pk[dataOff + 0]) & 0x0F;
              const color1 = nibble4(pk[dataOff + 1]) & 0x0F;
              const row = pk[dataOff + 2] & 0x1F; // 0..17
              const col = pk[dataOff + 3] & 0x3F; // 0..49
              for (let rrow = 0; rrow < 12; rrow++) {
                const patt = pk[dataOff + 4 + rrow];
                for (let cx = 0; cx < 6; cx++) {
                  const bit = (patt >> (5 - cx)) & 1;
                  const color = bit ? color1 : color0;
                  const x = col * 6 + cx;
                  const y = row * 12 + rrow;
                  if (x >= 0 && x < WIDTH && y >= 0 && y < HEIGHT) {
                    const idxp = (y * WIDTH + x);
                    const p = idxp * 4;
                    if (ins === 0x26) {
                      // XOR on color indexes, not RGB values
                      const prevIdx = indexRef.current[idxp] & 0x0F;
                      const nextIdx = prevIdx ^ (color & 0x0F);
                      indexRef.current[idxp] = nextIdx;
                      const rr = displayPaletteRef.current[nextIdx * 3 + 0];
                      const gg = displayPaletteRef.current[nextIdx * 3 + 1];
                      const bb = displayPaletteRef.current[nextIdx * 3 + 2];
                      frameRef.current[p + 0] = rr;
                      frameRef.current[p + 1] = gg;
                      frameRef.current[p + 2] = bb;
                      frameRef.current[p + 3] = 255;
                      if (!anyDrawRef.current && (rr | gg | bb) !== 0) anyDrawRef.current = true;
                    } else if ((color & 0x0F) === (transparentIndexRef.current & 0x0F)) {
                      // Write transparent index to clear this pixel (do not preserve old pixel)
                      indexRef.current[idxp] = transparentIndexRef.current & 0x0F;
                      const tr = paletteRef.current[(transparentIndexRef.current & 0x0F) * 3 + 0];
                      const tg = paletteRef.current[(transparentIndexRef.current & 0x0F) * 3 + 1];
                      const tb = paletteRef.current[(transparentIndexRef.current & 0x0F) * 3 + 2];
                      frameRef.current[p + 0] = tr;
                      frameRef.current[p + 1] = tg;
                      frameRef.current[p + 2] = tb;
                      frameRef.current[p + 3] = 255;
                    } else {
                      // Normal write: set color index
                      indexRef.current[idxp] = color;
                      const rr = displayPaletteRef.current[color * 3 + 0];
                      const gg = displayPaletteRef.current[color * 3 + 1];
                      const bb = displayPaletteRef.current[color * 3 + 2];
                      frameRef.current[p + 0] = rr;
                      frameRef.current[p + 1] = gg;
                      frameRef.current[p + 2] = bb;
                      frameRef.current[p + 3] = 255;
                      if (!anyDrawRef.current && (rr | gg | bb) !== 0) anyDrawRef.current = true;
                    }
                  }
                }
              }
              statsRef.current.tiles++;
              lastTileRef.current = { col, row, op: ins };
              break;
            }
            case 0x1C: { // Define Transparent Color (28)
              const idx = nibble4(pk[dataOff + 0]);
              transparentIndexRef.current = idx;
              break;
            }
            case 0x1E: // Load Color Table Low (entries 0..7)
            case 0x1F: { // Load Color Table High (entries 8..15)
              const base = ins === 0x1E ? 0 : 8;
              // Snapshot old palette segment for change detection
              const oldSeg = new Uint8Array(8 * 3);
              for (let i = 0; i < 8; i++) {
                const idx = base + i;
                oldSeg[i * 3 + 0] = paletteRef.current[idx * 3 + 0];
                oldSeg[i * 3 + 1] = paletteRef.current[idx * 3 + 1];
                oldSeg[i * 3 + 2] = paletteRef.current[idx * 3 + 2];
              }
              for (let i = 0; i < 8; i++) {
                // Each color uses 2 bytes: lower 4 bits of each nibble create RGB4
                const hi = pk[dataOff + i * 2];
                const lo = pk[dataOff + i * 2 + 1];
                let r4: number, g4: number, b4: number;
                if (paletteModeRef.current === 0) {
                  // Spec: Byte0 = 0000 RRRR, Byte1 = GGGG BBBB
                  r4 = hi & 0x0F;
                  g4 = (lo >> 4) & 0x0F;
                  b4 = lo & 0x0F;
                } else {
                  // Fallback observed in some encoders
                  r4 = hi & 0x0F;
                  g4 = (hi >> 4) & 0x0F;
                  b4 = lo & 0x0F;
                }
                const r = r4 * 17, g = g4 * 17, b = b4 * 17;
                const idx = (base + i);
                paletteRef.current[idx * 3 + 0] = r;
                paletteRef.current[idx * 3 + 1] = g;
                paletteRef.current[idx * 3 + 2] = b;
              }
              statsRef.current.palettes++;
              // Recompute display palette with recolor mapping
              recomputeDisplayPalette();
              // Heuristic clear: if any entries actually changed, clear pixels using those indices
              if (CLEAR_ON_PALETTE_CHANGE) {
                const changed: boolean[] = new Array(8).fill(false);
                for (let i = 0; i < 8; i++) {
                  const idx = base + i;
                  const o0 = oldSeg[i * 3 + 0];
                  const o1 = oldSeg[i * 3 + 1];
                  const o2 = oldSeg[i * 3 + 2];
                  const n0 = paletteRef.current[idx * 3 + 0];
                  const n1 = paletteRef.current[idx * 3 + 1];
                  const n2 = paletteRef.current[idx * 3 + 2];
                  if (o0 !== n0 || o1 !== n1 || o2 !== n2) changed[i] = true;
                }
                // If any changed, walk the active area and set matching indices to transparent
                let anyChanged = changed.some(Boolean);
                if (anyChanged) {
                  const tIdx = transparentIndexRef.current & 0x0F;
                  for (let y = BORDER_Y; y < HEIGHT - BORDER_Y; y++) {
                    for (let x = BORDER_X; x < WIDTH - BORDER_X; x++) {
                      const pIndex = (y * WIDTH + x);
                      const cur = indexRef.current[pIndex] & 0x0F;
                      const rel = cur - base; // 0..7 if in this segment
                      if (rel >= 0 && rel < 8 && changed[rel]) {
                        indexRef.current[pIndex] = tIdx;
                        const po = pIndex * 4;
                        // Set RGB to background/black to avoid ghosting when alpha not used
                        frameRef.current[po + 0] = paletteRef.current[tIdx * 3 + 0];
                        frameRef.current[po + 1] = paletteRef.current[tIdx * 3 + 1];
                        frameRef.current[po + 2] = paletteRef.current[tIdx * 3 + 2];
                        frameRef.current[po + 3] = 255;
                      }
                    }
                  }
                }
              }
              break;
            }
            default:
              // Scroll Preset (0x14) / Scroll Copy (0x18)
              // Decode per spec: byte0=color, byte1=hSCmd(4-5),hOffset(0-2), byte2=vSCmd(4-5),vOffset(0-3)
              if (ins === 0x14 || ins === 0x18) {
                const colorIdx = nibble4(pk[dataOff + 0]);
                const hByte = pk[dataOff + 1];
                const vByte = pk[dataOff + 2];
                const hSCmd = (hByte >> 4) & 0x03; // 0:none,1:left,2:right
                const vSCmd = (vByte >> 4) & 0x03; // 0:none,1:up,  2:down
                const hOffset = hByte & 0x07;      // fine horizontal (0..5)
                const vOffset = vByte & 0x0F;      // fine vertical   (0..11)
                // Coarse scroll by tile plus fine offset; left/up are negative
                const coarseDx = hSCmd === 1 ? -TILE_W : (hSCmd === 2 ? TILE_W : 0);
                const coarseDy = vSCmd === 1 ? -TILE_H : (vSCmd === 2 ? TILE_H : 0);
                const dx = (hSCmd === 1 ? -hOffset : (hSCmd === 2 ? hOffset : 0)) + coarseDx;
                const dy = (vSCmd === 1 ? -vOffset : (vSCmd === 2 ? vOffset : 0)) + coarseDy;
                const [fr, fg, fb] = [
                  displayPaletteRef.current[colorIdx * 3 + 0],
                  displayPaletteRef.current[colorIdx * 3 + 1],
                  displayPaletteRef.current[colorIdx * 3 + 2],
                ];
                // Shift only the active area; leave border intact
                const copyIdx = new Uint8Array(indexRef.current);
                for (let y = 0; y < ACTIVE_H; y++) {
                  for (let x = 0; x < ACTIVE_W; x++) {
                    const ax = x + BORDER_X;
                    const ay = y + BORDER_Y;
                    const sx = ax + dx;
                    const sy = ay + dy;
                    const dsti = (ay * WIDTH + ax);
                    const dst = dsti * 4;
                    if (sx >= BORDER_X && sx < WIDTH - BORDER_X && sy >= BORDER_Y && sy < HEIGHT - BORDER_Y) {
                      const srci = (sy * WIDTH + sx);
                      const srcIdx = copyIdx[srci] & 0x0F;
                      indexRef.current[dsti] = srcIdx;
                      frameRef.current[dst + 0] = displayPaletteRef.current[srcIdx * 3 + 0];
                      frameRef.current[dst + 1] = displayPaletteRef.current[srcIdx * 3 + 1];
                      frameRef.current[dst + 2] = displayPaletteRef.current[srcIdx * 3 + 2];
                      frameRef.current[dst + 3] = 255;
                     } else {
                      if (ins === 0x14) { // Preset: fill vacated area with color
                        indexRef.current[dsti] = colorIdx & 0x0F;
                        frameRef.current[dst + 0] = fr;
                        frameRef.current[dst + 1] = fg;
                        frameRef.current[dst + 2] = fb;
                        frameRef.current[dst + 3] = 255;
                      } else {
                        // Copy: wrap around inside active area per spec
                        const wrapX = (((sx - BORDER_X) % ACTIVE_W) + ACTIVE_W) % ACTIVE_W + BORDER_X;
                        const wrapY = (((sy - BORDER_Y) % ACTIVE_H) + ACTIVE_H) % ACTIVE_H + BORDER_Y;
                        const srci = (wrapY * WIDTH + wrapX);
                        const srcIdx = copyIdx[srci] & 0x0F;
                        indexRef.current[dsti] = srcIdx;
                        frameRef.current[dst + 0] = displayPaletteRef.current[srcIdx * 3 + 0];
                        frameRef.current[dst + 1] = displayPaletteRef.current[srcIdx * 3 + 1];
                        frameRef.current[dst + 2] = displayPaletteRef.current[srcIdx * 3 + 2];
                        frameRef.current[dst + 3] = 255;
                      }
                    }
                  }
                }
                anyDrawRef.current = anyDrawRef.current || ((fr | fg | fb) !== 0);
                statsRef.current.scrolls++;
                lastScrollRef.current = { dx, dy, op: ins };
              }
              break;
          }
        }
        lastIndexRef.current++;
      }

      // Push frame to canvas, respecting transparent color index (alpha)
      for (let y = 0; y < HEIGHT; y++) {
        for (let x = 0; x < WIDTH; x++) {
          const idxp = (y * WIDTH + x);
          const p = idxp * 4;
          const o = p;
          imageData.data[o + 0] = frameRef.current[p + 0];
          imageData.data[o + 1] = frameRef.current[p + 1];
          imageData.data[o + 2] = frameRef.current[p + 2];
          imageData.data[o + 3] = (indexRef.current[idxp] & 0x0F) === (transparentIndexRef.current & 0x0F) ? 0 : 255;
        }
      }
      ctx.putImageData(imageData, 0, 0);

      rafRef.current = requestAnimationFrame(render);

      // After 2 seconds with no visible draw, try palette fallback once
      if (!paletteFallbackTriedRef.current && t > 2 && !anyDrawRef.current) {
        paletteFallbackTriedRef.current = true;
        paletteModeRef.current = 1;
        // Reset state and replay from start with alternate palette mapping
        lastIndexRef.current = 0;
        for (let i = 0; i < 16; i++) {
          paletteRef.current[i * 3 + 0] = 0;
          paletteRef.current[i * 3 + 1] = 0;
          paletteRef.current[i * 3 + 2] = 0;
        }
        for (let i = 0; i < WIDTH * HEIGHT; i++) {
          const o = i * 4;
          frameRef.current[o + 0] = 0;
          frameRef.current[o + 1] = 0;
          frameRef.current[o + 2] = 0;
          frameRef.current[o + 3] = 255;
        }
        anyDrawRef.current = false;
        // eslint-disable-next-line no-console
        console.warn('[CdgPlayer] No visible graphics after 2s; retrying with palette fallback mode.');
      }

      // If still nothing after 4 seconds total, try ffmpeg fallback (when supported or opt-in)
      // Default is opt-in OR auto when no draw is detected, but only attempt once per mount.
      const urlHasOptIn = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('cdgFfmpeg'));
      const wantFfmpeg = urlHasOptIn || (!anyDrawRef.current && t > 4);
      if (!usingFfmpegFallback && wantFfmpeg && !ffmpegAttemptedRef.current && !ffmpegFailedRef.current) {
        void (async () => {
          try {
            setFfmpegDecoding(true);
            ffmpegAttemptedRef.current = true;
            // Lazy import to keep initial bundle small; use new 0.12 API with explicit core/worker/wasm URLs.
            if (!ffmpegModule || !ffmpegUtil) {
              const [mod, util] = await Promise.all([
                import('@ffmpeg/ffmpeg'),
                import('@ffmpeg/util')
              ]);
              ffmpegModule = mod as any; // { FFmpeg }
              ffmpegUtil = util as any;  // { toBlobURL }
            }
            const { FFmpeg } = ffmpegModule;
            const { toBlobURL } = ffmpegUtil;
            const ffmpeg = new FFmpeg();
            const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.7/dist/umd';
            await ffmpeg.load({
              coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
              wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
              workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
            });
            // Write CDG buffer
            await ffmpeg.writeFile('in.cdg', new Uint8Array(cdgBuffer));
            // Generate frames at 15 fps, scaled to 300x216 (nearest-neighbor)
            await ffmpeg.exec([
              '-fflags','+genpts','-f','cdg','-i','in.cdg',
              '-vf','scale=300:216:flags=neighbor,fps=15',
              'out_%05d.png'
            ]);
            // Read out frames
            const frames: {bitmap: ImageBitmap; url: string}[] = [];
            for (let i = 1; i < 200000; i++) {
              const name = `out_${String(i).padStart(5,'0')}.png`;
              try {
                const data = await ffmpeg.readFile(name) as Uint8Array;
                const blob = new Blob([data as unknown as BlobPart], { type: 'image/png' });
                const url = URL.createObjectURL(blob);
                const bitmap = await createImageBitmap(blob);
                frames.push({ bitmap, url });
              } catch {
                break;
              }
            }
            ffmpegFramesRef.current = frames;
            setUsingFfmpegFallback(true);
          } catch (err) {
            console.warn('[CdgPlayer] ffmpeg fallback failed', err);
            ffmpegFailedRef.current = true; // prevent repeated attempts per mount
          } finally {
            setFfmpegDecoding(false);
          }
        })();
      }
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Revoke object URLs and close bitmaps from ffmpeg frames
      const frames = ffmpegFramesRef.current;
      if (frames) {
        for (const f of frames) {
          try { f.bitmap.close(); } catch {}
          try { URL.revokeObjectURL(f.url); } catch {}
        }
      }
    };
  }, [cdgBuffer]);

  // Autoplay handling: attempt when the element can play
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onCanPlay = () => {
      setReady(true);
      void tryPlay();
    };
    const onPlay = () => setNeedsUserGesture(false);
    const onCanPlayThrough = () => {
      // Try again once fully buffered enough to play through
      if (!ready) setReady(true);
      void tryPlay();
    };
    const onError = (e: Event) => {
      // Surface minimal diagnostics in console for now
      // eslint-disable-next-line no-console
      console.warn('[CdgPlayer] audio error', e);
      setNeedsUserGesture(true);
    };
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('play', onPlay);
    el.addEventListener('canplaythrough', onCanPlayThrough);
    el.addEventListener('error', onError);
    // Try once if already buffered
    if (el.readyState >= 2) {
      setReady(true);
      void tryPlay();
    }
    return () => {
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('canplaythrough', onCanPlayThrough);
      el.removeEventListener('error', onError);
    };
  }, [tryPlay]);

  // Debug overlay removed

  return (
    <div className="w-full flex flex-col items-center">
      <div className="relative w-full flex justify-center">
        <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} className="bg-black w-full max-w-5xl h-auto rounded" />
        {needsUserGesture && (
          <button
            className="absolute inset-0 m-auto h-14 w-52 bg-green-600 hover:bg-green-500 text-white font-semibold rounded shadow-md"
            onClick={() => void tryPlay()}
            title="Start audio"
          >
            Tap to start audio
          </button>
        )}
        {/* CDG debug overlay removed */}
      </div>
      {(ffmpegDecoding || usingFfmpegFallback) && (
        <div className="mt-2 text-xs text-purple-200">
          {ffmpegDecoding ? 'Decoding CD+G with ffmpeg… (host-only fallback)' : 'ffmpeg fallback active'}
        </div>
      )}
      <audio
        ref={audioRef}
        src={mp3Url}
        controls
        autoPlay
        playsInline
        preload="auto"
        onEnded={onEnded}
        className="mt-2 w-full max-w-5xl"
      />
    </div>
  );
};

export default CdgPlayer;
